import {
  HiOutlineClipboardCheck,
  HiOutlineDocumentText,
  HiOutlineSearch,
  HiOutlineShieldCheck,
  HiOutlineSparkles,
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineChatAlt2,
  HiOutlineExclamation,
  HiOutlineLightningBolt,
  HiOutlineChartBar,
} from "react-icons/hi";
import { FiCalendar } from "react-icons/fi";

/** User-facing copy — keyed by API schema module / sub-feature keys */
export const MODULE_META = {
  ai_timesheet: {
    icon: HiOutlineClipboardCheck,
    accent: "blue",
    displayName: "Timesheet Intelligence",
    description:
      "Checks rostering timesheets at approval time so managers can investigate issues before sign-off. Findings are suggestions only. AI does not approve timesheets or change payroll.",
    childBriefs: {
      check_timesheet_anomalies:
        "Run AI anomaly analysis when approvers review timesheets.",
      bulk_check_timesheet_anomalies:
        "Analyse many timesheets in one background run from the timesheet page.",
      anomaly_read: "View previously saved anomaly results without re-running.",
      draft_note: "Generate draft shift notes from timesheet context.",
    },
    childSummaries: {
      check_timesheet_anomalies:
        "When a manager approves a timesheet, AI compares clocked hours against the rostered shift and flags possible issues such as unusual duration, overlaps, or missing breaks. Findings appear at approval time so the approver can investigate before signing off. AI does not approve or change payroll automatically.",
      bulk_check_timesheet_anomalies:
        "Lets approvers analyse every timesheet that matches a date range, worker, or participant in one run. The run continues in the background if the page is closed, rows that already have a report can be left out, and credits are used only for rows that finish. Needs Anomaly check to be on. AI does not approve or change timesheets.",
      anomaly_read:
        "Opens earlier anomaly results for a timesheet without running a new analysis. Useful when reviewing history or discussing a case with a worker. Does not replace a fresh check when circumstances have changed.",
      draft_note:
        "Creates a suggested shift or timesheet note using shift details, participant context, and recorded hours. Staff should review and edit before saving. Helps reduce blank notes after a shift.",
    },
  },
  ai_care_watch: {
    icon: HiOutlineShieldCheck,
    accent: "violet",
    displayName: "Care Watch",
    description:
      "A review queue for shifts that have already been worked. Groups incident concerns, missed documentation, and live floor state so coordinators can follow up in one place.",
    childBriefs: {
      auto_analyse:
        "When a shift ends, analyse the timesheet and send findings to Care Watch.",
    },
    childSummaries: {
      auto_analyse:
        "Runs analysis automatically when a worker ends a shift. Findings appear in Care Watch and each run uses AI credits. Care Watch itself stays available when this is off. Timesheet Anomaly check is a separate switch and does not control this.",
    },
  },
  ai_rostering: {
    icon: FiCalendar,
    accent: "indigo",
    description: "Smarter rostering suggestions and scheduling tools.",
    childBriefs: {
      worker_matching: "Suggest best-fit workers for open shifts.",
      manage: "AI tools for roster managers (optimisation, overlap resolution).",
    },
    childSummaries: {
      worker_matching:
        "When filling an open shift, AI suggests workers who may be suitable based on availability, skills, location, and participant requirements. Suggestions help rosterers decide faster; the rosterer always chooses who to assign.",
      manage:
        "Adds AI-assisted tools for roster managers, such as spotting scheduling conflicts and suggesting adjustments across the roster. All changes remain under manager control.",
    },
  },
  ai_case_note: {
    icon: HiOutlineDocumentText,
    accent: "sky",
    description: "Help coordinators write clearer, compliant case notes.",
    childBriefs: {
      assistant: "Inline assist while editing case notes.",
      draft: "Generate first-draft case note content.",
      quality_scoring: "Score note quality against NDIS expectations.",
    },
    childSummaries: {
      assistant:
        "Provides in-context suggestions while a staff member writes or edits a case note — for example clearer wording or prompts for missing detail. The author must review anything before saving.",
      draft:
        "Builds an initial case note from participant context, recent activity, and the note type. Saves time on first drafts; staff must review and correct before the note is final.",
      quality_scoring:
        "Reviews a case note against common NDIS documentation expectations and gives a quality indication. Helps coordinators improve standards; scores are guidance only and do not block saving.",
    },
  },
  ai_shift: {
    icon: HiOutlineLightningBolt,
    accent: "amber",
    description: "Shift handover and end-of-shift summaries.",
    childBriefs: {
      handover: "Summarise shift details for the next worker or coordinator.",
    },
    childSummaries: {
      handover:
        "Produces a concise handover summary from shift notes, tasks, incidents, and participant information so the next worker or coordinator can pick up quickly. Staff should confirm critical details before relying on the summary alone.",
    },
  },
  ai_copilot: {
    icon: HiOutlineSparkles,
    accent: "purple",
    displayName: "Vertex360 Copilot",
    description:
      "Ask operational questions in plain language. Copilot reads participants, workers, rosters, funding, and incidents, then returns an answer, a table, or a clarifying question. It never updates payroll or participant records.",
    childBriefs: {},
    childSummaries: {},
  },
  ai_incident: {
    icon: HiOutlineExclamation,
    accent: "rose",
    description: "Incident reportability and investigation support.",
    childBriefs: {
      reportability: "Assess whether an incident must be reported.",
      investigation_draft: "Draft investigation narrative from incident data.",
    },
    childSummaries: {
      reportability:
        "Helps staff assess whether an incident may need reporting under NDIS or internal policy, based on the details entered. Provides guidance only — it is not legal advice and does not replace your organisation's incident procedures.",
      investigation_draft:
        "Creates a structured first draft of an investigation narrative using incident fields, timelines, and related notes. Investigators must review, edit, and approve all content before it is shared or filed.",
    },
  },
  ai_funding: {
    icon: HiOutlineCurrencyDollar,
    accent: "emerald",
    description: "Funding utilisation forecasts and budget insights.",
    childBriefs: {
      budget_forecast: "Plain-English budget burn and headroom forecasts.",
    },
    childSummaries: {
      budget_forecast:
        "Explains how participant or plan funding is tracking over time — spend rate, remaining headroom, and simple forecasts in plain language. Helps coordinators plan services; verify against official NDIS plans and finance records.",
    },
  },
  ai_claim: {
    icon: HiOutlineChartBar,
    accent: "teal",
    description: "Pre-validate NDIS claims before submission.",
    childBriefs: {
      validation: "Flag claim issues before billing review.",
    },
    childSummaries: {
      validation:
        "Checks NDIS claims for common issues — such as line items, dates, or support categories — before submission or final billing review. Flags possible problems for staff to fix; final approval stays with your billing team.",
    },
  },
  ai_compliance: {
    icon: HiOutlineShieldCheck,
    accent: "slate",
    description: "Compliance forecasting and document intelligence.",
    childBriefs: {
      forecast: "Predict upcoming compliance gaps across your workforce.",
      hrm_insights: "AI insights across HRM documents and worker records.",
      worker_insights: "AI insights for individual worker compliance context.",
    },
    childSummaries: {
      forecast:
        "Highlights workers or documents that may become non-compliant soon (for example expiring certifications). Helps managers act before deadlines. Confirm against source documents in your system.",
      hrm_insights:
        "Surfaces patterns and summaries across HR and worker compliance documents to help managers spot gaps or trends. Supports workforce compliance planning; does not replace formal audits.",
      worker_insights:
        "Provides summarised insight for a worker's compliance status, documents, and related activity to help managers support that worker. Restrict access via role permissions.",
    },
  },
  ai_participant: {
    icon: HiOutlineUserGroup,
    accent: "blue",
    description: "Participant insights, goals, and semantic search.",
    childBriefs: {
      goal_progress: "Analyse progress toward participant goals.",
      semantic_search: "Search documents and notes by meaning, not just keywords.",
      medication_adherence: "Surface medication adherence patterns.",
    },
    childSummaries: {
      goal_progress:
        "Reviews goal-related notes and activity to summarise how a participant is progressing toward stated goals. Helps coordinators prepare reviews; interpretation should involve the support team and participant or representative.",
      semantic_search:
        "Finds relevant participant documents and notes even when exact words differ. Speeds up file review for coordinators. Results depend on indexed content in your organisation.",
      medication_adherence:
        "Highlights patterns in recorded medication administration — missed doses, timing trends, or gaps — to support care planning. For coordination and review only; not medical advice.",
    },
  },
  ai_search: {
    icon: HiOutlineSearch,
    accent: "gray",
    description: "Organisation-wide semantic search (when enabled as a module).",
    childBriefs: {
      semantic_search: "Search organisation content by meaning.",
      embedding_generate: "Index content for semantic search (background).",
    },
    childSummaries: {
      semantic_search:
        "Search across allowed organisation content using natural meaning rather than exact keywords. Users only see what their role permits.",
      embedding_generate:
        "Prepares organisation content so semantic search can find it accurately. Runs as a background capability when enabled. Uses AI credits when indexing new content.",
    },
  },
  ai_feedback: {
    icon: HiOutlineChatAlt2,
    accent: "orange",
    description: "Analyse participant and staff feedback at scale.",
    childBriefs: {
      analysis: "Summarise themes and sentiment from feedback submissions.",
    },
    childSummaries: {
      analysis:
        "Groups feedback and complaints into themes, trends, and sentiment so managers can see what participants and staff are raising most often. Summaries are indicative — read underlying submissions for full context.",
    },
  },
  ai_invoice: {
    icon: HiOutlineCurrencyDollar,
    accent: "cyan",
    description: "NDIS invoice and line-item assistance.",
    childBriefs: {
      code_suggestion: "Suggest Price Guide codes — human review required before save.",
    },
    childSummaries: {
      code_suggestion:
        "Suggests NDIS Price Guide line codes based on service description and context. Every suggestion must be reviewed by billing staff before saving or submitting. Incorrect codes affect claims and revenue.",
    },
  },
};

/** Collect brief + summary strings for search */
export const moduleSearchText = (moduleKey, moduleMeta) => {
  const ui = MODULE_META[moduleKey] || {};
  const parts = [
    moduleMeta?.label,
    ui.description,
    ...Object.keys(moduleMeta?.children || {}).flatMap((childKey) => [
      childMetaLabelFromSchema(moduleMeta?.children?.[childKey]),
      ui.childBriefs?.[childKey],
      ui.childSummaries?.[childKey],
    ]),
  ];
  return parts.filter(Boolean).join(" ");
};

export const childMetaLabelFromSchema = (meta) =>
  typeof meta === "string" ? meta : meta?.label || "";

/** Whether module or sub-feature is shipped (from API schema.available). */
export const isSchemaAvailable = (meta) => Boolean(meta?.available);

/**
 * Shown on AI Settings but locked: dimmed, read-only, "Coming soon".
 * Applies even when the API still marks the module available.
 * Care Watch is not listed: its switch follows the organisation setting.
 */
export const LOCKED_AI_MODULES = new Set();

export const isLockedAiModule = (moduleKey) => LOCKED_AI_MODULES.has(moduleKey);

/** Interactive only when the API has shipped it and the UI has not locked it. */
export const isModuleEditable = (moduleKey, meta) =>
  !isLockedAiModule(moduleKey) && isSchemaAvailable(meta);

/**
 * Sort schema entries so integrated features appear first and "Coming soon" last.
 * Stable within each group — does not sort by enabled state.
 */
export const sortSchemaEntries = (entries) =>
  [...entries].sort(([aKey, a], [bKey, b]) => {
    const aRank = isModuleEditable(aKey, a) ? 0 : 1;
    const bRank = isModuleEditable(bKey, b) ? 0 : 1;
    return aRank - bRank;
  });

/**
 * Sub-features that only work while another one is on. Mirrors the backend
 * AiFeatures::clampUnavailable rule (bulk runs go through the anomaly check).
 */
export const CHILD_REQUIRES = {
  ai_timesheet: {
    bulk_check_timesheet_anomalies: "check_timesheet_anomalies",
  },
};

export const requiredChildFor = (moduleKey, childKey) =>
  CHILD_REQUIRES[moduleKey]?.[childKey] || null;

export const dependentChildrenOf = (moduleKey, childKey) =>
  Object.entries(CHILD_REQUIRES[moduleKey] || {})
    .filter(([, required]) => required === childKey)
    .map(([dependent]) => dependent);

export const getChildBrief = (moduleKey, childKey, label) => {
  const ui = MODULE_META[moduleKey] || {};
  return ui.childBriefs?.[childKey] || label;
};

export const getChildSummary = (moduleKey, childKey, brief) => {
  const ui = MODULE_META[moduleKey] || {};
  return ui.childSummaries?.[childKey] || brief;
};

export const ACCENT_STYLES = {
  teal: {
    ring: "ring-teal-200",
    bg: "bg-teal-50",
    text: "text-teal-700",
    iconBg: "bg-teal-100",
  },
  violet: {
    ring: "ring-violet-200",
    bg: "bg-violet-50",
    text: "text-violet-700",
    iconBg: "bg-violet-100",
  },
  indigo: {
    ring: "ring-indigo-200",
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    iconBg: "bg-indigo-100",
  },
  sky: {
    ring: "ring-sky-200",
    bg: "bg-sky-50",
    text: "text-sky-700",
    iconBg: "bg-sky-100",
  },
  amber: {
    ring: "ring-amber-200",
    bg: "bg-amber-50",
    text: "text-amber-700",
    iconBg: "bg-amber-100",
  },
  purple: {
    ring: "ring-purple-200",
    bg: "bg-purple-50",
    text: "text-purple-700",
    iconBg: "bg-purple-100",
  },
  rose: {
    ring: "ring-rose-200",
    bg: "bg-rose-50",
    text: "text-rose-700",
    iconBg: "bg-rose-100",
  },
  emerald: {
    ring: "ring-emerald-200",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
  },
  teal: {
    ring: "ring-teal-200",
    bg: "bg-teal-50",
    text: "text-teal-700",
    iconBg: "bg-teal-100",
  },
  slate: {
    ring: "ring-slate-200",
    bg: "bg-slate-50",
    text: "text-slate-700",
    iconBg: "bg-slate-100",
  },
  blue: {
    ring: "ring-blue-200",
    bg: "bg-[#ECF0FD]",
    text: "text-[#4B6CB7]",
    iconBg: "bg-[#ECF0FD]",
  },
  gray: {
    ring: "ring-gray-200",
    bg: "bg-gray-50",
    text: "text-gray-700",
    iconBg: "bg-gray-100",
  },
  orange: {
    ring: "ring-orange-200",
    bg: "bg-orange-50",
    text: "text-orange-700",
    iconBg: "bg-orange-100",
  },
  cyan: {
    ring: "ring-cyan-200",
    bg: "bg-cyan-50",
    text: "text-cyan-700",
    iconBg: "bg-cyan-100",
  },
};
